import { useEffect, useMemo, useState } from '../../vendor/react';
import { useOperations } from '../operations/useOperations';
import { buildBusinessReport, defaultReportPeriod } from '../reports/report-engine';
import { answerBusinessQuestion, buildAiInsights } from '../ai/ai-engine';
import {
  approveInventoryTransfer,
  cancelInventoryTransfer,
  createInventoryTransfer,
  ensurePhase8,
  enrollCustomerMembership,
  getPhase8,
  prepareInventoryTransfer,
  processMembershipRenewals,
  receiveInventoryTransfer,
  recordAudit,
  recordMembershipPayment,
  redeemMembershipBenefit,
  saveMembershipPlan,
  setCustomerMembershipStatus,
  shipInventoryTransfer,
  subscribePhase8,
} from './phase8-store';
import type { ReportPeriod } from '../../types/domain';

export function usePhase8(period: ReportPeriod = defaultReportPeriod(), reportBranchId = '') {
  const operations = useOperations();
  const businessId = operations.business?.id ?? '';
  const branchId = operations.branch?.id ?? '';
  const userId = operations.session?.user.id ?? 'unknown';
  const [revision, setRevision] = useState(0);
  useEffect(() => subscribePhase8(() => setRevision((value) => value + 1)), []);
  useEffect(() => { if (businessId) { ensurePhase8(businessId); processMembershipRenewals(businessId); } }, [businessId]);
  const snapshot = useMemo(() => businessId ? getPhase8(businessId) ?? ensurePhase8(businessId) : null, [businessId, revision]);
  const branches = operations.workspace?.branches.filter((item) => item.businessId === businessId) ?? [];
  const report = useMemo(() => {
    if (!snapshot || !operations.catalog || !operations.sales.snapshot || !operations.snapshot) return null;
    return buildBusinessReport({ period, branches, sales: operations.sales.snapshot, catalog: operations.catalog, operations: operations.snapshot, phase8: snapshot, branchId: reportBranchId || undefined });
  }, [snapshot, operations.catalog, operations.sales.snapshot, operations.snapshot, period.from, period.to, reportBranchId, revision]);
  return {
    ...operations,
    phase8: snapshot,
    report,
    branches,
    transfers: snapshot?.transfers ?? [],
    transferHistory: snapshot?.transferHistory ?? [],
    membershipPlans: snapshot?.membershipPlans ?? [],
    memberships: snapshot?.memberships ?? [],
    membershipCharges: snapshot?.membershipCharges ?? [],
    membershipFulfillments: snapshot?.membershipFulfillments ?? [],
    auditEvents: snapshot?.auditEvents ?? [],
    aiInsights: report && snapshot && operations.catalog && operations.sales.snapshot && operations.snapshot ? buildAiInsights({ report, catalog: operations.catalog, sales: operations.sales.snapshot, operations: operations.snapshot, phase8: snapshot }) : [],
    actions: {
      createTransfer: (input: Omit<Parameters<typeof createInventoryTransfer>[1], 'requestedBy'>) => createInventoryTransfer(businessId, { ...input, requestedBy: userId }),
      approveTransfer: (transferId: string) => approveInventoryTransfer(businessId, transferId, userId),
      prepareTransfer: (transferId: string) => prepareInventoryTransfer(businessId, transferId, userId),
      shipTransfer: (transferId: string) => shipInventoryTransfer(businessId, transferId, userId),
      receiveTransfer: (transferId: string, quantities: Record<string, number>) => receiveInventoryTransfer(businessId, transferId, quantities, userId),
      cancelTransfer: (transferId: string, reason: string) => cancelInventoryTransfer(businessId, transferId, userId, reason),
      saveMembershipPlan: (input: Parameters<typeof saveMembershipPlan>[1]) => saveMembershipPlan(businessId, input, userId),
      enrollMembership: (input: Omit<Parameters<typeof enrollCustomerMembership>[1], 'userId'>) => enrollCustomerMembership(businessId, { ...input, userId }),
      payMembershipCharge: (chargeId: string, input: Omit<Parameters<typeof recordMembershipPayment>[2], 'branchId' | 'userId' | 'cashControlEnabled'>) => recordMembershipPayment(businessId, chargeId, { ...input, branchId, userId, cashControlEnabled: operations.cashControlEnabled }),
      redeemBenefit: (membershipId: string, input: Omit<Parameters<typeof redeemMembershipBenefit>[2], 'branchId' | 'userId'>) => redeemMembershipBenefit(businessId, membershipId, { ...input, branchId, userId }),
      setMembershipStatus: (membershipId: string, status: Parameters<typeof setCustomerMembershipStatus>[2]) => setCustomerMembershipStatus(businessId, membershipId, status, userId),
      askNexo: (question: string) => {
        if (!report || !snapshot || !operations.catalog || !operations.sales.snapshot || !operations.snapshot) throw new Error('Todavía no hay datos suficientes.');
        const answer = answerBusinessQuestion(question, { report, catalog: operations.catalog, sales: operations.sales.snapshot, operations: operations.snapshot, phase8: snapshot });
        recordAudit(businessId, { branchId, userId, entityType: 'ai', entityId: answer.id, action: 'ai.question', summary: question, metadata: { tools: answer.toolsUsed.join(',') } });
        return answer;
      },
      audit: (input: Omit<Parameters<typeof recordAudit>[1], 'userId'>) => recordAudit(businessId, { ...input, userId }),
    },
  };
}
